import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { lighten, styled } from "@mui/material/styles";
import {
  DataGridPro,
  GridRowEditStartReasons,
  GridRowEditStopReasons,
  GridRowModesModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { UploadyProvider } from "@sinch/components/form/UploadInput/UploadyProvider";
import { PendingChangeDecisionEnum } from "@sinch/core/entities/serverEnums";
import { useFormReady, useMuiForm } from "@sinch/hooks/useMuiForm";
import React, { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, get, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { pendingProfileChangeSchema, PendingProfileChangeSchemaFormFields } from "../formSchema";
import { PendingChangesRow, usePendingChangeColumns } from "./pendingChangesColumns";
import type { WorkerPendingChangesQuery } from "../queries.types";
import { usePendingChangeReview } from "./usePendingChangeReview";
import { useSettings } from "@sinch/core/providers/AppSettings";
import Typography from "@mui/material/Typography";
import { WorkerPendingChanges } from "@sinch/components/application/ProfileReviewDialog/queries.graphql";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import { parseAttributeValue } from "@sinch/components/application/Worker/Attributes/tools";
import { MdsIcon } from "@sinch/components";
import { MdsCheck, MdsClose } from "@sinch/icons";
import { equals } from "ramda";
import { isNotNil } from "ramda-adjunct";

type PendingChange = WorkerPendingChangesQuery["pendingProfileChange"][number];

interface PendingChangesReviewProps {
  workerId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const useBuildDefaultValues = (changes: PendingChange[]): PendingProfileChangeSchemaFormFields => {
  const { timeZone } = useSettings();
  return useMemo(
    () => ({
      attributes: changes.map((item) => ({
        attributeId: item.attributeId,
        type: item.fieldName === null ? "attribute" : "system",
        value: parseAttributeValue(item.dataType ?? null, item.pendingValue, item.pendingFiles ?? [], timeZone),
        fieldName: item.fieldName,
        expirationDate: item.expirationDate ?? null,
        changeId: item.id,
        decision: null,
        note: "",
      })),
    }),
    [changes, timeZone]
  );
};

const rowClassFor = (decision: PendingChangeDecisionEnum | null): string =>
  match(decision)
    .with(PendingChangeDecisionEnum.Approved, () => "AttributeDataGridRow-approved")
    .with(PendingChangeDecisionEnum.Rejected, () => "AttributeDataGridRow-rejected")
    .otherwise(() => "");

export const PendingChangesReview: FC<PendingChangesReviewProps> = ({ workerId, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const { timeZone } = useSettings();

  const { data: pendingData, refetch } = useSuspenseQueryHandler<WorkerPendingChangesQuery>(
    WorkerPendingChanges,
    { workerId },
    { fetchPolicy: "network-only" }
  );
  const changes = pendingData?.pendingProfileChange;

  const columns = usePendingChangeColumns(changes?.some((change) => change.expirable) ?? false);

  const defaultValues = useBuildDefaultValues(changes!);
  const form = useMuiForm({
    schema: pendingProfileChangeSchema,
    formOptions: { defaultValues },
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  const { handleReviewChanges } = usePendingChangeReview({ workerId, onSuccess: () => {}, changes: changes ?? [] });

  const rows = useMemo<PendingChangesRow[]>(
    () =>
      changes?.map((change, index) => ({
        ix: index,
        decision: null,
        ...change,
        currentValue: change.currentValue,
        pendingValue: change.pendingValue,
      })) ?? [],
    [changes, timeZone]
  );

  const submitReview = form.handleSubmit(async (data) => {
    const response = await handleReviewChanges(data);
    if (!response) {
      return;
    }
    // Close the dialog once everything is reviewed. If the admin only reviewed
    // a subset, the remaining rows are reloaded so they can finish the rest.
    const refetched = await refetch();
    if ((refetched.data?.pendingProfileChange?.length ?? 0) === 0) {
      (onSuccess ?? onClose)();
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const handleRowModesModelChange = (newModel: GridRowModesModel) => {
    setIsEditing(Object.keys(newModel).length > 0);
  };

  if (changes?.length === 0) {
    return (
      <DialogContent>
        <Typography>{t("ProfileReview.noPendingChanges")}</Typography>
      </DialogContent>
    );
  }

  return (
    <FormProvider {...form}>
      <UploadyProvider onFinish={() => form.setReady(true)} onStart={() => form.setReady(false)}>
        <DialogContent>
          <BulkDecisionButtons apiRef={apiRef} />
          <Box sx={{ display: "flex" }}>
            <PendingChangesDataGrid<PendingChangesRow>
              apiRef={apiRef}
              columns={columns}
              disableColumnFilter
              disableColumnMenu
              disableColumnPinning
              disableColumnReorder
              disableColumnResize
              disableColumnSorting
              disableRowSelectionOnClick
              disableVirtualization
              editMode="row"
              getRowClassName={(params) => rowClassFor(params.row.decision)}
              getRowHeight={() => "auto"}
              getRowId={(row) => row.ix}
              isCellEditable={({ row, field }) =>
                match(field)
                  .with("pendingValue", () => row.editable)
                  .with("expirationDate", () => row.expirable)
                  .otherwise(() => true)
              }
              onRowEditStart={(params, event) => {
                if (
                  params.reason === GridRowEditStartReasons.cellDoubleClick &&
                  params.field !== "pendingValue" &&
                  params.field !== "expirationDate"
                ) {
                  event.defaultMuiPrevented = true;
                  return;
                }
              }}
              onRowEditStop={(params, event) => {
                if (params.reason === GridRowEditStopReasons.rowFocusOut) {
                  event.defaultMuiPrevented = true;
                  return;
                }
              }}
              onRowModesModelChange={handleRowModesModelChange}
              processRowUpdate={(updatedRow) => {
                const ix = +updatedRow.ix;
                form.setValue(`attributes.${ix}.value`, updatedRow.pendingValue);
                form.setValue(`attributes.${ix}.expirationDate`, updatedRow.expirationDate);
                // Deep-compare — gallery/option arrays and Date objects are re-created
                // on every render, so a reference (!==) compare flips unchanged rows to
                // "Approved with admin edit" and ships the parsed value as if edited.
                const defaultVal = get(form.formState.defaultValues, `attributes.${ix}.value`);
                if (!equals(defaultVal, updatedRow.pendingValue)) {
                  form.setValue(`attributes.${ix}.decision`, PendingChangeDecisionEnum.Approved);
                  return { ...updatedRow, decision: PendingChangeDecisionEnum.Approved };
                }
                return updatedRow;
              }}
              rows={rows}
              slots={{ footer: () => null }}
            />
          </Box>
        </DialogContent>
        <ReviewActions disabled={isEditing} onClose={onClose} onSubmit={submitReview} />
      </UploadyProvider>
    </FormProvider>
  );
};

interface BulkDecisionButtonsProps {
  apiRef: ReturnType<typeof useGridApiRef>;
}

const BulkDecisionButtons: FC<BulkDecisionButtonsProps> = ({ apiRef }) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<PendingProfileChangeSchemaFormFields>();
  const attributes = useWatch<PendingProfileChangeSchemaFormFields, "attributes">({ name: "attributes" });

  const allApproved =
    attributes.length > 0 && attributes.every((attribute) => attribute.decision === PendingChangeDecisionEnum.Approved);
  const allRejected =
    attributes.length > 0 && attributes.every((attribute) => attribute.decision === PendingChangeDecisionEnum.Rejected);

  const applyBulkDecision = (decision: PendingChangeDecisionEnum, isActive: boolean) => {
    const next = isActive ? null : decision;
    setValue(
      "attributes",
      attributes.map((a) => ({ ...a, decision: next })),
      { shouldDirty: true, shouldTouch: true }
    );
    apiRef.current?.updateRows(attributes.map((_, ix) => ({ ix, decision: next })));
  };

  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1, justifyContent: "flex-end" }}>
      <Button
        color="success"
        data-cy="approve-all-btn"
        onClick={() => applyBulkDecision(PendingChangeDecisionEnum.Approved, allApproved)}
        size="small"
        startIcon={<MdsIcon color={allApproved ? "inherit" : "success"} icon={MdsCheck} />}
        variant={allApproved ? "contained" : "text"}
      >
        {t("ProfileReview.approveAll")}
      </Button>
      <Button
        color="error"
        data-cy="reject-all-btn"
        onClick={() => applyBulkDecision(PendingChangeDecisionEnum.Rejected, allRejected)}
        size="small"
        startIcon={<MdsIcon color={allRejected ? "inherit" : "error"} icon={MdsClose} />}
        variant={allRejected ? "contained" : "text"}
      >
        {t("ProfileReview.rejectAll")}
      </Button>
    </Box>
  );
};

interface ReviewActionsProps {
  disabled: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

const ReviewActions: FC<ReviewActionsProps> = ({ disabled, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const isReady = useFormReady();
  const attributes = useWatch<PendingProfileChangeSchemaFormFields, "attributes">({ name: "attributes" });
  // Submitting with every row left undecided reports "changes reviewed" while the
  // whole queue stays pending, so require at least one approval or rejection.
  const hasDecision = attributes.some((attribute) => isNotNil(attribute.decision));

  return (
    <DialogActions>
      {!hasDecision && (
        <Typography color="text.secondary" sx={{ mr: "auto" }} variant="body2">
          {t("ProfileReview.decisionRequired")}
        </Typography>
      )}
      <Button data-cy="profile-review-cancel-btn" onClick={onClose} variant="text">
        {t("cancel")}
      </Button>
      <Button
        color="success"
        data-cy="submit-review-btn"
        disabled={disabled || !isReady || !hasDecision}
        onClick={onSubmit}
        variant="contained"
      >
        {t("ProfileReview.submitReview")}
      </Button>
    </DialogActions>
  );
};

const PendingChangesDataGrid = styled(DataGridPro)(({ theme }) => ({
  width: "100%",
  "& .MuiDataGrid-cell.MuiDataGrid-cell--textLeft": { paddingRight: "1px" },
  "& .MuiDataGrid-row": { minHeight: "44px !important" },
  "& .MuiDataGrid-cell.MuiDataGrid-cell--editing": {
    boxShadow: "none",
    "&:focus-within": { outline: "none" },
  },
  "& .AttributeDataGridRow-note, & .AttributeDataGridRow-note.MuiDataGrid-cell--textRight": {
    paddingLeft: 0,
    paddingRight: 0,
  },
  "& .MuiDataGrid-row--editing": { boxShadow: "none" },
  "& .MuiDataGrid-actionsCell": { gridGap: 0 },
  "& .AttributeDataGridRow-approved": {
    backgroundColor: lighten(theme.palette.success.light, 0.9),
  },
  "& .AttributeDataGridRow-rejected": {
    backgroundColor: lighten(theme.palette.error.light, 0.9),
  },
})) as typeof DataGridPro;
